import React, { useState, useEffect } from 'react';
import { previewDataset } from '../api';
import LoadingSpinner from './LoadingSpinner';
import { InfoTooltip, EmptyState, DataTable } from './shared/UIComponents';

function DatasetPreview({ datasetName }) {
  const [selectedDataset, setSelectedDataset] = useState(datasetName || '');
  const [previewRows, setPreviewRows] = useState(10);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update selectedDataset when datasetName prop changes
  useEffect(() => {
    if (datasetName) {
      setSelectedDataset(datasetName);
    }
  }, [datasetName]);

  const handlePreviewDataset = async () => {
    if (!selectedDataset) {
      setError('Please select a dataset to preview.');
      return;
    }
    setLoading(true);
    setError(null);
    setPreviewData(null);
    try {
      const result = await previewDataset(selectedDataset, previewRows);
      setPreviewData(result);
    } catch (err) {
      setError(err.message || 'Failed to preview dataset. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTableColumns = () => {
    if (!previewData || !previewData.columns) return [];
    return previewData.columns.map(col => ({ header: col, accessor: col }));
  }

  return (
    <div className="dataset-preview">
      <InfoTooltip 
        title="Dataset Preview" 
        content="Quickly preview your dataset to see its structure, columns, and sample data. Useful for understanding your data before analysis."
      >
        <h2 className="dataset-preview__title">Dataset Preview</h2>
      </InfoTooltip>
      
      <div className="dataset-preview__form-group">
        <label className="dataset-preview__label">Dataset Name:</label>
        <input 
          type="text" 
          value={selectedDataset} 
          onChange={e => setSelectedDataset(e.target.value)} 
          placeholder="Enter dataset name"
          className="dataset-preview__input"
          disabled={loading}
        />
      </div>
      
      <div className="dataset-preview__form-group">
        <label className="dataset-preview__label">Number of Rows:</label>
        <input 
          type="number" 
          value={previewRows} 
          onChange={e => setPreviewRows(Number(e.target.value))} 
          min="1" 
          max="100"
          className="dataset-preview__input"
          disabled={loading}
        />
      </div>
      
      <button 
        onClick={handlePreviewDataset} 
        disabled={loading || !selectedDataset}
        className="dataset-preview__preview-btn"
      >
        {loading ? (
          <LoadingSpinner size="small" text="Loading..." />
        ) : (
          'Preview Dataset'
        )}
      </button>
      
      {loading && (
        <div className="dataset-preview__loading">
          <LoadingSpinner text="Loading dataset preview..." />
        </div>
      )}
      
      {error && (
        <div className="dataset-preview__error">
          <strong>Error:</strong> {error}
        </div>
      )}
      
      {!selectedDataset && !loading && !previewData && !error && (
        <EmptyState
          title="No dataset selected"
          description="Enter or select a dataset name, then click Preview Dataset."
          icon="📁"
        />
      )}

      {previewData && !loading && (
        <div className="dataset-preview__results">
          <h3 className="dataset-preview__results-title">Dataset Preview</h3>
          <DataTable
            columns={getTableColumns()}
            data={previewData.preview_data}
          />  
        </div>
      )}
    </div>
  );
}

export default DatasetPreview;
